import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterOutlet } from '@angular/router';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { PistonLoaderComponent } from './components/piston-loader/piston-loader.component';
import { ServerErrorModalComponent } from './components/server-error-modal/server-error-modal.component';
import { DealerConfig } from './interfaces';
import { TranslatePipe } from './pipes/translate.pipe';
import { DealerService } from './services/dealer.service';
import { activeLanguage, t } from './services/translate';
import { TranslationLoaderService } from './services/translation-loader.service';

const QUERY_PARAM_DEALER_ID = 'dealerId';

function dealerIdParam(value: unknown): string | null {
  if (Array.isArray(value)) {
    return '';
  }
  return typeof value === 'string' ? value : null;
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, PistonLoaderComponent, ServerErrorModalComponent, TranslatePipe],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .missing-dealer-id {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        padding: 1.5rem;
        background: #f5f5f5;
      }
      .missing-dealer-id__card {
        max-width: 32rem;
        padding: 2rem 2.25rem;
        background: #fff;
        border-radius: 0.5rem;
        box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.08);
        text-align: center;
      }
      .missing-dealer-id__title {
        margin: 0 0 0.75rem;
        font-size: 1.5rem;
        font-weight: 600;
      }
      .missing-dealer-id__body {
        margin: 0;
        font-size: 1rem;
        line-height: 1.5;
        color: #333;
      }
    `,
  ],
})
export class App {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dealerService = inject(DealerService);
  private readonly translationLoader = inject(TranslationLoaderService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  protected readonly title = signal('wpp');
  protected readonly loading = signal(true);

  // Shell loader covers both startup fetches (dealer config + translation overlay). They run in
  // parallel; without the overlay condition the gate paints raw cids for a beat on every refresh.
  protected readonly showLoader = computed(
    (): boolean => this.loading() || !this.translationLoader.ready(),
  );

  /**
   * Gate: every entry into WPP must carry a `?dealerId=` query param. Until the backend
   * decides on a default-vs-required policy for the `x-dealer-id` header, we refuse to
   * boot the app without one and ask the user to contact support for the proper URL.
   * Flip this gate off by reintroducing a fallback in `setCurrentDealerId` (or in the
   * interceptor) once that decision is in.
   */
  protected readonly dealerIdMissing = computed(
    (): boolean => this.dealerService.currentDealerId() === null,
  );

  protected dealerId: string | null =
    this.activatedRoute.snapshot.queryParamMap.get(QUERY_PARAM_DEALER_ID);

  /**
   * Resolves the dealer-id from the URL and loads its config (theming + the
   * global `dealerConfig` state). We deliberately do NOT debounce: that only
   * added a fixed 500 ms startup delay. Instead we wait for the router's first
   * completed navigation — see `firstNavigationSettled()` — so the query params
   * are reliable, then react to them immediately and on every later change.
   */
  protected readonly config = toSignal<DealerConfig>(
    this.firstNavigationSettled()
      .pipe(switchMap((): Observable<Params> => this.activatedRoute.queryParams))
      .pipe(
        map((queryParams: Params): string | null =>
          dealerIdParam(queryParams[QUERY_PARAM_DEALER_ID]),
        ),
      )
      // React only when the dealer-id itself changes. Unrelated query-param
      // changes — and the safety net re-attaching `dealerId` to the URL — must
      // not trigger a config refetch.
      .pipe(distinctUntilChanged())
      // Only overwrite the stored dealer-id when the URL actually carries a value.
      // `null` here means a sub-page navigation dropped the query param — the safety
      // net above will restore it in the URL, and the service should keep the last
      // known id so the interceptor doesn't lose the header between navigations.
      .pipe(
        tap((id: string | null): void => {
          if (id !== null) {
            this.dealerService.setCurrentDealerId(id);
          }
          // The initial navigation has settled. If there is still no dealer-id,
          // there is no config to fetch — drop the loader so the "missing dealer"
          // gate can take over. Until `firstNavigationSettled()` emits, `loading`
          // stays true, so the gate never flashes during startup.
          if (this.dealerService.currentDealerId() === null) {
            this.loading.set(false);
          }
        }),
      )
      .pipe(
        map((id: string | null): string | null =>
          id === null ? null : this.dealerService.currentDealerId(),
        ),
      )
      .pipe(filter((id: string | null): id is string => id !== null))
      .pipe(tap((): void => this.loading.set(true)))
      .pipe(
        switchMap(
          (dealerId: string): Observable<DealerConfig> =>
            this.dealerService
              .getDealerConfig(dealerId)
              .pipe(
                catchError((error: unknown): Observable<DealerConfig> => {
                  if (
                    error instanceof HttpErrorResponse &&
                    error.status === HttpStatusCode.NotFound
                  ) {
                    this.dealerService.setCurrentDealerId(null);
                  }
                  return of({} as DealerConfig);
                }),
              )
              .pipe(tap((): void => this.loading.set(false))),
        ),
      ),
  );
  public constructor() {
    // Sync set so the very first HTTP request already carries the header.
    this.dealerService.setCurrentDealerId(this.dealerId);

    // We intentionally do NOT decide here whether to show the "missing dealer"
    // gate: at construction time the initial route snapshot may not be settled
    // yet, so `dealerId` can read as null even when the URL has it. The gate
    // decision is deferred to the query-params stream below — until that emits,
    // `loading` stays true and the loader is shown, so the gate never flashes.

    // Safety net: many `router.navigate(...)` call sites in WPP don't pass
    // `queryParamsHandling: 'merge'`, which would drop `?dealerId=` on navigation. Rather
    // than touching every call site (29+), we re-attach the param centrally after each
    // navigation. Uses `replaceUrl: true` so it doesn't pollute the browser history, and
    // is a no-op when the URL already carries the param — so there's no navigation loop.
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((): void => this.ensureDealerIdInUrl());

    // The global `scrollbar-gutter: stable` (styles/_post.scss) reserves ~15px for
    // the scrollbar on every page. The loader and "missing dealer" screens are
    // short and never scroll, so that reserved gutter just shows as an empty strip.
    // Drop the gutter for those two app-shell states only — the real app flow keeps
    // `stable`, so navigation between short and long pages never shifts horizontally.
    effect((): void => {
      const isAppShellScreen: boolean = this.showLoader() || this.dealerIdMissing();
      this.document.documentElement.classList.toggle('app-no-scrollbar-gutter', isAppShellScreen);
    });
    this.destroyRef.onDestroy((): void => {
      this.document.documentElement.classList.remove('app-no-scrollbar-gutter');
    });

    // index.html ships static Dutch head tags; re-apply them from the catalogue so they follow
    // the overlay and the active language. Static copy stays until the overlay has landed —
    // never a cid in the tab title.
    effect((): void => {
      if (!this.translationLoader.ready()) {
        return;
      }
      this.document.title = t('wpp.app.meta.title');
      this.document.documentElement.lang = activeLanguage();
      this.document
        .querySelector('meta[name="description"]')
        ?.setAttribute('content', t('wpp.app.meta.description'));
    });
  }

  private ensureDealerIdInUrl(): void {
    const currentId: string | null = this.dealerService.currentDealerId();
    if (!currentId) {
      return;
    }

    if (this.activatedRoute.snapshot.queryParamMap.get(QUERY_PARAM_DEALER_ID)) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { [QUERY_PARAM_DEALER_ID]: currentId },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Emits exactly once, as soon as the router's initial navigation has completed
   * and the query params are therefore reliable. If navigation already completed
   * before this component was constructed, it emits immediately; otherwise it
   * waits for the first `NavigationEnd`. This replaces the old `debounceTime` —
   * which only guessed, with a fixed delay, that the route had settled.
   */
  private firstNavigationSettled(): Observable<void> {
    if (this.router.navigated) {
      return of(undefined);
    }
    return this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      take(1),
      map((): void => undefined),
    );
  }
}
